/**
 * WorkoutSetsTracker
 *
 * Shows per-set tracking for a single exercise during an active session.
 * Props:
 *   item          – the UserWorkoutPlanItem (sets_planned, reps_planned, weight_planned, rest_seconds, exercise_item)
 *   setsData      – array of set objects: [{ set, reps, weight, done, completedAt }]
 *   onSetUpdate   – (index, patch) => void  — caller updates setsData
 *   onComplete    – () => void  — called when all sets are done
 *   isDone        – bool  — exercise already completed
 */
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSpinner, faPen } from "@fortawesome/free-solid-svg-icons";
import styles from "./WorkoutSetsTracker.module.css";

export const DEFAULT_REST_SECONDS = 60; // seconds

// ── Timed (isometric) exercise helpers ───────────────────────────────────
// Retorna true se reps_planned usa tempo (ex: "45s" ou "45s,45s,60s")
export function isTimedExercise(repsPlanned) {
  if (!repsPlanned) return false;
  const first = String(repsPlanned).split(",")[0].trim();
  return /^\d+s$/i.test(first);
}

// Extrai os segundos de uma série específica dentro de reps_planned
export function getTimedSeconds(repsPlanned, setIndex) {
  if (!repsPlanned) return null;
  const parts = String(repsPlanned).split(",").map(p => p.trim());
  const val = parts[setIndex] ?? parts[parts.length - 1] ?? parts[0];
  const match = val.match(/^(\d+)s$/i);
  return match ? parseInt(match[1], 10) : null;
}

export function RestTimer({ seconds, onDone }) {
  const endTimeRef = useRef(Date.now() + seconds * 1000);
  const [remaining, setRemaining] = useState(seconds);
  const tickRef = useRef(null);
  const onDoneRef = useRef(onDone);
  // Som/vibração de fim de descanso removidos em 2026-09-18 (pedido direto
  // do usuário, ver docs/07-issues.md): no iOS o loop do timer é suspenso
  // pelo SO quando o app está em segundo plano, então o som nunca tocava no
  // cenário mais comum (aluno saiu pra outro app durante o descanso); e
  // quando tocava (app em primeiro plano), pausava qualquer mídia externa
  // (Spotify, YouTube, etc.) sem retomar depois, exigindo ação manual do
  // aluno. Vibração nunca funcionou no iOS (Safari não implementa a
  // Vibration API). Custo maior que o benefício nos dois casos — timer
  // segue 100% funcional visualmente (contagem, cor, conclusão).

  useEffect(() => { onDoneRef.current = onDone; });

  useEffect(() => {
    const tick = () => {
      const r = Math.ceil((endTimeRef.current - Date.now()) / 1000);
      if (r <= 1) {
        setRemaining(0);
        onDoneRef.current();
        return;
      }
      setRemaining(r);
      tickRef.current = setTimeout(tick, 250);
    };

    // When returning from another app, recalculate immediately.
    // visibilitychange sozinho nao e confiavel em PWA standalone no iOS (o
    // WebKit as vezes nao dispara ao voltar de outro app/tela bloqueada) -
    // focus/pageshow servem de segunda rede de seguranca pro mesmo recalculo.
    const onVisible = () => {
      if (!document.hidden) {
        clearTimeout(tickRef.current);
        tick();
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("pageshow", onVisible);
    tick();

    return () => {
      clearTimeout(tickRef.current);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pct = Math.round(((seconds - remaining) / seconds) * 100);
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  // Cálculo para o arco circular (SVG stroke-dashoffset)
  // Circunferência = 2πr, com r=55 → circunf ≈ 345
  const radius = 55;
  const circleCircumference = 2 * Math.PI * radius;
  const strokeOffset = circleCircumference - (pct / 100) * circleCircumference;
  const rotation = (pct / 100) * 360;

  return (
    <div className={styles.restTimer}>
      <div className={styles.restLabel}>⏱ Descanso</div>

      {/* Circular analog countdown */}
      <svg className={styles.restCircle} viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
        {/* Background circle */}
        <circle cx="60" cy="60" r={radius} fill="none" stroke="var(--border-0-10)" strokeWidth="3" />

        {/* Rotating indicator (thin line at top) */}
        <line
          x1="60"
          y1={60 - radius + 5}
          x2="60"
          y2={60 - radius - 2}
          stroke="var(--blue)"
          strokeWidth="2"
          strokeLinecap="round"
          style={{
            transformOrigin: "60px 60px",
            transform: `rotate(${rotation}deg)`,
            transition: "transform 0.25s linear",
          }}
        />

        {/* Progress arc */}
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="var(--blue)"
          strokeWidth="3"
          strokeDasharray={circleCircumference}
          strokeDashoffset={strokeOffset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 0.25s linear",
            transformOrigin: "60px 60px",
            transform: "rotate(-90deg)",
          }}
        />

        {/* Center text */}
        <text
          x="60"
          y="70"
          textAnchor="middle"
          fontSize="40"
          fontWeight="800"
          fill="var(--blue)"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {mins > 0 ? `${mins}:${String(secs).padStart(2, "0")}` : `${secs}s`}
        </text>
      </svg>

      <button className={styles.restSkip} onClick={(e) => { e.stopPropagation(); onDone(); }}>Pular descanso</button>
    </div>
  );
}

export function TimedSetRow({ setNum, targetSeconds, done, onDone }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDone = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      onDone({ reps: 1, weight: null });
    } finally {
      setTimeout(() => setIsSubmitting(false), 200);
    }
  };

  if (done) {
    return (
      <div className={`${styles.setRowTimed} ${styles.setRowDone}`}>
        <span className={styles.setNum}>{setNum}</span>
        <span className={styles.setVal}>{targetSeconds}s</span>
        <span className={styles.setDoneTag}><FontAwesomeIcon icon={faCheck} /></span>
      </div>
    );
  }

  return (
    <div className={styles.setRowTimed}>
      <span className={styles.setNum}>{setNum}</span>
      <span className={styles.timedTarget}>{targetSeconds}s</span>
      <button
        className={styles.setDoneBtn}
        aria-label={`Confirmar série ${setNum} - ${targetSeconds}s`}
        onClick={handleDone}
        disabled={isSubmitting}
        style={{ opacity: isSubmitting ? 0.6 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
      >
        <FontAwesomeIcon icon={isSubmitting ? faSpinner : faCheck} />
      </button>
    </div>
  );
}

export function isValidReps(val) {
  // Antes exigia um número positivo. Mas o professor pode planejar a série
  // como "até a falha" ou um range tipo "8 a 12" (reps_planned aceita texto
  // livre — ver WorkoutAdminModal), e nesses casos o aluno não tem uma
  // quantidade exata pra informar. O backend já lida com isso: sets_log é
  // JSON livre e o cálculo de volume (app/utils/workout_volume.py::_numero)
  // já extrai o primeiro número de qualquer texto, tratando "falha" como 0
  // sem quebrar. Então aqui só exige que o campo não esteja vazio.
  return String(val).trim().length > 0;
}

export function SetRow({ setNum, repsDefault, weightDefault, done, onDone, onError, prevReps, prevWeight, prevDate, requireWeight, editing, onEditStart, onEditCancel, onEdit }) {
  const [reps, setReps] = useState(repsDefault != null ? String(repsDefault) : "");
  const [weight, setWeight] = useState(weightDefault != null ? String(weightDefault) : "");
  const [repsInvalid, setRepsInvalid] = useState(false);
  const [weightInvalid, setWeightInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs always hold the latest value — safe to read inside onClick without stale closure
  const repsRef = useRef(reps);
  const weightRef = useRef(weight);
  const repsInputRef = useRef(null);
  const weightInputRef = useRef(null);
  const buttonRef = useRef(null);

  // Sync when defaults arrive after initial mount (e.g. after initSetsForItem resolves)
  useEffect(() => {
    if (repsDefault != null) setReps(prev => {
      const v = prev === "" ? String(repsDefault) : prev;
      repsRef.current = v;
      return v;
    });
  }, [repsDefault]);

  // Clear error highlight as soon as user starts typing
  const handleRepsChange = (e) => {
    repsRef.current = e.target.value;
    setReps(e.target.value);
    if (repsInvalid) setRepsInvalid(false);
  };

  // Handle Enter key in reps input → focus weight input
  const handleRepsKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (requireWeight) {
        weightInputRef.current?.focus();
      } else {
        // If weight not required, confirm series
        handleDone();
      }
    }
  };

  // Handle Enter key in weight input → confirm series
  const handleWeightKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDone();
    }
  };

  // Confirm series (extracted to function for reuse)
  const handleDone = () => {
    // Prevent double-submit
    if (isSubmitting) return;

    const r = repsRef.current;
    const w = weightRef.current;
    if (!isValidReps(r)) {
      setRepsInvalid(true);
      if (onError) onError('Informe as repetições realizadas (um número, ou "falha" quando for até a falha).');
      return;
    }
    if (requireWeight && (w === "" || w === null || w === undefined)) {
      setWeightInvalid(true);
      if (onError) onError("Informe o peso utilizado neste exercício.");
      return;
    }
    setWeightInvalid(false);

    // Set submitting state to prevent double-click
    setIsSubmitting(true);

    // Se o texto digitado for um número "limpo" (o caso comum), grava como
    // número — mantém o formato histórico do sets_log. Senão, grava o texto
    // como veio ("falha", "8 a 10", etc.) — reps_planned já aceita texto
    // livre nesses casos, e o backend sabe extrair o número quando existir
    // (ver app/utils/workout_volume.py::_numero).
    const rTrim = String(r).trim();
    const rNum = Number(rTrim);
    const repsValue = rTrim !== "" && Number.isFinite(rNum) ? rNum : rTrim;

    // Call onDone (which will handle async operations)
    try {
      onDone({ reps: repsValue, weight: w ? parseFloat(w) : null });
    } finally {
      // Reset submitting state after a brief delay to ensure backend processed
      setTimeout(() => setIsSubmitting(false), 200);
    }
  };

  useEffect(() => {
    if (weightDefault != null) setWeight(prev => {
      const v = prev === "" ? String(weightDefault) : prev;
      weightRef.current = v;
      return v;
    });
  }, [weightDefault]);

  // ── Correção de uma série já registrada ──
  const snapshotRef = useRef(null);
  useEffect(() => {
    if (editing) {
      snapshotRef.current = { reps: repsRef.current, weight: weightRef.current };
      repsInputRef.current?.focus();
    }
  }, [editing]);

  const handleEditCancel = () => {
    const snap = snapshotRef.current;
    if (snap) {
      repsRef.current = snap.reps; setReps(snap.reps);
      weightRef.current = snap.weight; setWeight(snap.weight);
    }
    setRepsInvalid(false);
    setWeightInvalid(false);
    if (onEditCancel) onEditCancel();
  };

  const handleEditSave = () => {
    const r = repsRef.current;
    const w = String(weightRef.current ?? "").trim();
    if (!isValidReps(r)) {
      setRepsInvalid(true);
      if (onError) onError('Informe as repetições realizadas (um número, ou "falha" quando for até a falha).');
      return;
    }
    if (requireWeight && w === "") {
      setWeightInvalid(true);
      if (onError) onError("Informe o peso utilizado neste exercício.");
      return;
    }
    const wNum = w === "" ? null : parseFloat(w.replace(",", "."));
    if (wNum !== null && !Number.isFinite(wNum)) {
      setWeightInvalid(true);
      if (onError) onError("Informe o peso em kg (apenas números).");
      return;
    }
    const rTrim = String(r).trim();
    const rNum = Number(rTrim);
    const repsValue = rTrim !== "" && Number.isFinite(rNum) ? rNum : rTrim;
    setRepsInvalid(false);
    setWeightInvalid(false);
    repsRef.current = String(repsValue); setReps(String(repsValue));
    weightRef.current = wNum === null ? "" : String(wNum); setWeight(wNum === null ? "" : String(wNum));
    if (onEdit) onEdit({ reps: repsValue, weight: wNum });
  };

  if (done && editing) {
    return (
      <div className={styles.setRowWrap}>
        <div className={`${styles.setRow} ${styles.setRowEditing}`}>
          <span className={styles.setNum}>{setNum}</span>
          <input
            ref={repsInputRef}
            className={`${styles.setInput}${repsInvalid ? " " + styles.setInputError : ""}`}
            type="text"
            inputMode="text"
            placeholder="reps ou falha"
            aria-label={`Corrigir repetições, série ${setNum}`}
            value={reps}
            onChange={handleRepsChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); if (requireWeight) weightInputRef.current?.focus(); else handleEditSave(); }
              if (e.key === "Escape") handleEditCancel();
            }}
          />
          <input
            ref={weightInputRef}
            className={`${styles.setInput}${weightInvalid ? " " + styles.setInputError : ""}`}
            type="text"
            inputMode="decimal"
            placeholder={requireWeight ? "kg *" : "kg"}
            aria-label={`Corrigir carga em kg, série ${setNum}${requireWeight ? " (obrigatório)" : ""}`}
            value={weight}
            onChange={e => { weightRef.current = e.target.value; setWeight(e.target.value); if (weightInvalid) setWeightInvalid(false); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleEditSave(); }
              if (e.key === "Escape") handleEditCancel();
            }}
          />
          <button
            type="button"
            className={styles.setDoneBtn}
            aria-label={`Salvar correção da série ${setNum}`}
            onClick={handleEditSave}
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        </div>
        <button type="button" className={styles.editCancel} onClick={handleEditCancel}>Cancelar</button>
      </div>
    );
  }

  if (done) {
    return (
      <div className={`${styles.setRow} ${styles.setRowDone}`}>
        <span className={styles.setNum}>{setNum}</span>
        <span className={styles.setVal}>{reps || "-"}</span>
        <span className={styles.setVal}>{weight || "-"}</span>
        {onEditStart ? (
          <button
            type="button"
            className={styles.setEditBtn}
            aria-label={`Corrigir série ${setNum}`}
            title="Corrigir reps e carga"
            onClick={onEditStart}
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        ) : (
          <span className={styles.setDoneTag}><FontAwesomeIcon icon={faCheck} /></span>
        )}
      </div>
    );
  }

  const hasPrev = prevReps != null || prevWeight != null;

  function fillPrev() {
    if (prevReps != null) { repsRef.current = String(prevReps); setReps(String(prevReps)); }
    if (prevWeight != null) { weightRef.current = String(prevWeight); setWeight(String(prevWeight)); }
  }

  return (
    <div className={styles.setRowWrap}>
      {hasPrev && (
        <div className={styles.prevHint} onClick={fillPrev} title="Clique para preencher com dados do treino anterior">
          <span className={styles.prevLabel}>📋 Treino ant.:</span>
          {prevReps != null && <span className={styles.prevFill}>{prevReps} reps</span>}
          {prevReps != null && prevWeight != null && <span>×</span>}
          {prevWeight != null && <span className={styles.prevFill}>{prevWeight}kg</span>}
        </div>
      )}
      <div className={styles.setRow}>
        <span className={styles.setNum}>{setNum}</span>
        <input
          ref={repsInputRef}
          className={`${styles.setInput}${repsInvalid ? " " + styles.setInputError : ""}`}
          type="text"
          inputMode="text"
          placeholder="reps ou falha"
          title={`Repetições (um número, ou "falha" quando o treino pede até a falha)`}
          aria-label={`Repetições, série ${setNum} - número, ou "falha" quando for até a falha`}
          aria-required="true"
          value={reps}
          onChange={handleRepsChange}
          onKeyDown={handleRepsKeyDown}
        />
        <input
          ref={weightInputRef}
          className={`${styles.setInput}${weightInvalid ? " " + styles.setInputError : ""}`}
          type="text"
          inputMode="decimal"
          pattern="[0-9]*[.,]?[0-9]*"
          placeholder={requireWeight ? "kg *" : "kg"}
          title={`Carga em kg${requireWeight ? " (obrigatório)" : ""}`}
          aria-label={`Carga em kg, série ${setNum}${requireWeight ? " (obrigatório)" : ""}`}
          aria-required={requireWeight}
          data-required={requireWeight}
          value={weight}
          onChange={e => { weightRef.current = e.target.value; setWeight(e.target.value); if (weightInvalid) setWeightInvalid(false); }}
          onKeyDown={handleWeightKeyDown}
        />
        <button
          ref={buttonRef}
          className={styles.setDoneBtn}
          aria-label={`Confirmar série ${setNum}`}
          onClick={handleDone}
          disabled={isSubmitting}
          style={{
            opacity: isSubmitting ? 0.6 : 1,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
          }}
        >
          <FontAwesomeIcon icon={isSubmitting ? faSpinner : faCheck} style={{ animation: isSubmitting ? 'spin 1s linear infinite' : 'none' }} />
        </button>
      </div>
    </div>
  );
}

export default function WorkoutSetsTracker({ item, setsData, onSetUpdate, onComplete, isDone, onRestStart, onRestEnd, isResting, previousSets, onError, onSuccess, exerciseIndex, totalExercises, completedSoFar }) {
  const [restingAfterSet, setRestingAfterSet] = useState(null); // set index that just finished
  const [editingIndex, setEditingIndex] = useState(null);       // série em correção (lápis)
  const [expanded, setExpanded] = useState(false);              // exercício concluído: "Ver e editar"
  // Guarda os sets finais sincronizados para uso em handleRestDone (evita stale closure)
  const pendingFinalSetsRef = useRef(null);

  // All hooks MUST be at the top, before any conditional returns
  const prevIsRestingRef = useRef(isResting);
  const trackerRef = useRef(null);

  // Sincroniza quando o header "Pular" é clicado (isResting vai de true → false externamente)
  useEffect(() => {
    if (prevIsRestingRef.current && !isResting && restingAfterSet !== null) {
      // Header pulou o descanso — limpa o estado local do card
      setRestingAfterSet(null);
      const finalSets = pendingFinalSetsRef.current;
      if (finalSets && finalSets.every(s => s.done)) onComplete(finalSets); // idempotente
    }
    prevIsRestingRef.current = isResting;
  }, [isResting]); // eslint-disable-line react-hooks/exhaustive-deps

  const sets = setsData || [];
  const completedCount = sets.filter(s => s.done).length;
  const allSetsDone = sets.length > 0 && completedCount === sets.length;

  // Scroll to tracker when all sets complete
  useEffect(() => {
    if (allSetsDone && trackerRef.current) {
      trackerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [allSetsDone]);
  // rest_seconds stores seconds; may be comma-separated per set (e.g. "60,60,90,90")
  function getRestSecondsForSet(setIndex) {
    const raw = (item?.rest_seconds || "").toString().trim();
    const parts = raw.split(",").map(s => parseFloat(s.trim())).filter(v => !isNaN(v) && v > 0);
    return parts.length > 0
      ? (parts[setIndex] ?? parts[parts.length - 1] ?? DEFAULT_REST_SECONDS)
      : DEFAULT_REST_SECONDS;
  }
  const restSeconds = getRestSecondsForSet(restingAfterSet ?? 0);

  // Se está em descanso (mesmo que isDone já seja true), mostra o timer no card
  if (isDone && restingAfterSet === null) {
    const total = item?.sets_planned || setsData?.length || 0;
    const canEdit = sets.length > 0 && !isTimedExercise(item?.reps_planned);
    return (
      <div className={styles.doneWrap}>
        <div className={styles.doneRow}>
          <span className={styles.doneIcon}><FontAwesomeIcon icon={faCheck} /></span>
          <span className={styles.doneLabel}>
            {total} {total === 1 ? "série" : "séries"} concluídas
          </span>
          {canEdit && (
            <button
              type="button"
              className={styles.doneEditBtn}
              aria-expanded={expanded}
              onClick={() => { setExpanded(v => !v); setEditingIndex(null); }}
            >
              {expanded ? "Fechar" : "Ver e editar"}
            </button>
          )}
        </div>
        {canEdit && expanded && renderTable(true)}
      </div>
    );
  }

  function renderTable(pencils = false) {
    return (
        <div className={styles.setTable}>
          {isTimedExercise(item?.reps_planned) ? (
            <>
              <div className={styles.setTableTimedHeader}>
                <span>#</span>
                <span>Alvo</span>
                <span></span>
              </div>
              {sets.map((s, i) => (
                <TimedSetRow
                  key={i}
                  setNum={i + 1}
                  targetSeconds={getTimedSeconds(item?.reps_planned, i)}
                  done={s.done}
                  onDone={(data) => handleSetDone(i, data)}
                />
              ))}
            </>
          ) : (
            <>
              <div className={styles.setTableHeader}>
                <span>#</span>
                <span>Reps</span>
                <span>Kg</span>
                <span></span>
              </div>
              {sets.map((s, i) => {
                const prevSets = previousSets?.sets;
                // Se o histórico tem menos séries que o planejado (ex: aluno fez 1 de 4),
                // usa a última série real como hint para as linhas sem correspondência.
                const prevSet = prevSets?.[i] ?? prevSets?.[prevSets.length - 1];
                return (
                  <SetRow
                    key={i}
                    setNum={i + 1}
                    repsDefault={s.reps ?? item?.reps_planned}
                    weightDefault={s.weight ?? item?.weight_planned}
                    done={s.done}
                    onDone={(data) => handleSetDone(i, data)}
                    onError={onError}
                    editing={editingIndex === i}
                    onEditStart={pencils ? () => setEditingIndex(i) : undefined}
                    onEditCancel={() => setEditingIndex(null)}
                    onEdit={(data) => handleSetEdit(i, data)}
                    prevReps={prevSet?.reps ?? null}
                    prevWeight={prevSet?.weight ?? null}
                    prevDate={i === 0 ? previousSets?.date : null}
                    requireWeight={
                      item?.exercise_item?.movement_type === 'forca' &&
                      item?.exercise_item?.equipment !== 'peso_corporal'
                    }
                  />
                );
              })}
            </>
          )}
        </div>
    );
  }

  // Série que acabou de ser registrada (durante o descanso): aviso com atalho "Corrigir"
  const justDoneSet = restingAfterSet !== null ? sets[restingAfterSet] : null;
  const justDone = !!justDoneSet?.done && !isTimedExercise(item?.reps_planned);
  const justDoneText = (() => {
    if (!justDone) return "";
    const r = justDoneSet.reps;
    const repsTxt = r !== null && r !== undefined && r !== "" && Number.isFinite(Number(r)) ? `${r} reps` : String(r ?? "-");
    const w = justDoneSet.weight;
    return w !== null && w !== undefined && w !== "" ? `${repsTxt} x ${w} kg` : repsTxt;
  })();

  // Durante o descanso a tabela fica escondida: mostra só a série em correção
  function renderEditRow() {
    const st = sets[editingIndex];
    return (
      <SetRow
        key={`edit-${editingIndex}`}
        setNum={editingIndex + 1}
        repsDefault={st.reps}
        weightDefault={st.weight}
        done
        editing
        onEditCancel={() => setEditingIndex(null)}
        onEdit={(data) => handleSetEdit(editingIndex, data)}
        onError={onError}
        requireWeight={
          item?.exercise_item?.movement_type === 'forca' &&
          item?.exercise_item?.equipment !== 'peso_corporal'
        }
      />
    );
  }

  function handleSetEdit(index, data) {
    const patch = { reps: data.reps, weight: data.weight };
    onSetUpdate(index, patch);
    if (pendingFinalSetsRef.current) {
      pendingFinalSetsRef.current = pendingFinalSetsRef.current.map((st, i) => (i === index ? { ...st, ...patch } : st));
    }
    setEditingIndex(null);
    if (onSuccess) onSuccess(`Série ${index + 1} corrigida`);
  }

  function handleSetDone(index, data) {
    const patch = { ...data, done: true, completedAt: new Date().toISOString() };
    onSetUpdate(index, patch);
    const builtSets = sets.map((s, i) => i === index ? { ...s, ...patch } : s);
    pendingFinalSetsRef.current = builtSets;

    // Se todas as séries estão concluídas, marca o exercício como completo AGORA —
    // não espera o descanso terminar. Isso garante que um refresh/logout durante o
    // descanso não deixe o exercício travado.
    if (builtSets.every(s => s.done)) {
      // Mostrar toast apenas se não for o último exercício
      if (exerciseIndex !== undefined && totalExercises !== undefined && exerciseIndex < totalExercises - 1) {
        const completedCount = (completedSoFar ?? 0) + 1;
        if (onSuccess) onSuccess(`Exercício ${completedCount} de ${totalExercises} finalizado`);
      }
      onComplete(builtSets);
    }

    setRestingAfterSet(index);
    const thisRestSeconds = getRestSecondsForSet(index); // usa índice exato, não estado ainda-antigo
    if (onRestStart) onRestStart(thisRestSeconds, item?.exercise_item?.name || item?.name || "Exercício", item?.id);
  }

  function handleRestDone() {
    setRestingAfterSet(null);
    if (onRestEnd) onRestEnd();
    // onComplete já foi chamado em handleSetDone quando todas as séries foram concluídas.
    // completeExercise é idempotente (guard completions[id]), então chamar novamente é seguro
    // apenas para séries intermediárias que ainda não finalizaram o exercício.
    const finalSets = pendingFinalSetsRef.current;
    const allDone = finalSets ? finalSets.every(s => s.done) : sets.every(s => s.done);
    if (allDone) onComplete(finalSets);
  }

  return (
    <div className={styles.tracker} ref={trackerRef}>
      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${sets.length ? (completedCount / sets.length) * 100 : 0}%` }}
          />
        </div>
        <span className={styles.progressLabel}>
          {completedCount}/{sets.length} séries
          {allSetsDone && <span style={{ marginLeft: '8px', color: 'var(--blue)', fontWeight: 800 }}>✓ Pronto!</span>}
        </span>
      </div>

      {restingAfterSet !== null ? (
        <>
          <RestTimer seconds={restSeconds} onDone={handleRestDone} />
          {justDone && editingIndex === null && (
            <div className={styles.setBanner} role="status" aria-live="polite">
              <span>Série {restingAfterSet + 1}: {justDoneText}</span>
              <button type="button" className={styles.setBannerBtn} onClick={() => setEditingIndex(restingAfterSet)}>
                Corrigir
              </button>
            </div>
          )}
          {editingIndex !== null && sets[editingIndex]?.done && renderEditRow()}
        </>
      ) : (
        renderTable()
      )}
    </div>
  );
}
